/* istanbul ignore file */

import {
  type useLastInteractedStore,
  useLastInteractedStoreMachine,
} from '@order/LastInteractedStore';

import { type ReorderOrder } from '../../useReorderOrderLineItem.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * The location used for reorder should follow this list of priority:
 * 1. Active Cart (from last interacted store machine).
 * 2. Last menu (from last interacted store machine).
 * 3. Most recent order (from the reorder query on the screen).
 *
 * Note: We are not using the last order from the last interacted store machine.
 * Instead we use the already provided most recent order, to avoid another query.
 * This is because `useLastValidOrder` is cache-only and is only queried from `OrderProductConfirmProductLoaded`.
 */
export const useReorderOrderLineItemLocation = (
  params: UseReorderOrderLineItemParams,
) => {
  const { mostRecentOrder } = params;

  const [state] = useLastInteractedStoreMachine();
  const { interactedStores: stores } = state.context;
  const lastInteractedStore = stores.activeCart || stores.menu;

  // ─── Last Interacted Store ───────────────────────────────────────────

  if (lastInteractedStore) {
    return {
      restaurantId: lastInteractedStore.restaurantId,
      restaurantName: lastInteractedStore.restaurantName,
      restaurantSlug: lastInteractedStore.restaurantSlug,
      deliveryOrderDetails: getDeliveryOrderDetails(lastInteractedStore),
    };
  }

  // ─── Most Recent Order ───────────────────────────────────────────────

  return {
    restaurantId: mostRecentOrder?.restaurant?.id,
    restaurantName: mostRecentOrder?.restaurant?.slug,
    restaurantSlug: mostRecentOrder?.restaurant?.name,
    deliveryOrderDetails: mostRecentOrder?.deliveryOrderDetail,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getDeliveryOrderDetails = (order?: LastInteractedStore) => {
  if (!order?.addressId || !order?.vendor || !order?.vendorRestaurantId) {
    return undefined;
  }

  return {
    addressId: order.addressId,
    tip: 0,
    deliveryFee: order.deliveryFee ?? 0,
    vendor: order.vendor,
    vendorRestaurantId: order.vendorRestaurantId,
    address: { id: order.addressId },
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type LastInteractedStore = ReturnType<typeof useLastInteractedStore>;

type UseReorderOrderLineItemParams = {
  mostRecentOrder: ReorderOrder | undefined;
};
